import { api } from './api';
import {
  CancellationFee,
  EventItem,
  EventListFilters,
  IccDomain,
  PaginatedResponse,
  TicketTier,
} from '../types/api';

export interface EventCreatePayload {
  title: string;
  description: string;
  /** OBLIGATOIRE — domaine ICC UNESCO */
  iccDomain: IccDomain;
  iccSubcategory?: string;
  city: string;
  venue: string;
  startsAt: string;
  endsAt: string;
  coverUrl?: string;
  /** L'organisateur approuve la mise en ligne immédiate (visible sans KYC). */
  publish?: boolean;
  /** Personnalisation du billet par l'organisateur (optionnelle). */
  ticketDesign?: {
    accentColor?: string;
    bannerUrl?: string;
    message?: string;
    organizerLogoUrl?: string;
    blocks?: Array<{ type: string; visible: boolean }>;
  };
  tiers: Array<Omit<TicketTier, 'id' | 'sold'> & { id?: string }>;
}

export const eventsService = {
  list: async (filters: EventListFilters = {}): Promise<PaginatedResponse<EventItem>> => {
    const { data } = await api.get<PaginatedResponse<EventItem>>('/events', { params: filters });
    return data;
  },

  detail: async (id: string): Promise<EventItem> => {
    const { data } = await api.get<EventItem>(`/events/${id}`);
    return data;
  },

  create: async (payload: EventCreatePayload): Promise<EventItem> => {
    const { data } = await api.post<EventItem>('/events', payload);
    return data;
  },

  update: async (id: string, payload: Partial<EventCreatePayload>): Promise<EventItem> => {
    const { data } = await api.patch<EventItem>(`/events/${id}`, payload);
    return data;
  },

  /**
   * Annulation (bug 14) : si aucun billet n'a été vendu, l'événement est
   * supprimé immédiatement (204, `deleted: true`). Sinon la requête passe en
   * attente de validation admin et l'API renvoie l'événement mis à jour
   * (status CANCELLATION_PENDING, `deleted: false`).
   */
  requestCancellation: async (
    id: string,
  ): Promise<{ deleted: boolean; event: EventItem | null; cancellationTaxXAF: number }> => {
    const res = await api.delete(`/events/${id}`);
    if (res.status === 204) return { deleted: true, event: null, cancellationTaxXAF: 0 };
    const event = res.data as EventItem;
    return { deleted: false, event, cancellationTaxXAF: event.cancellationTaxXAF ?? 0 };
  },

  /** Toujours accessible, même compte verrouillé — voir ProtectedRoute. */
  myCancellationFees: async (): Promise<CancellationFee[]> => {
    const { data } = await api.get<CancellationFee[]>('/events/cancellation-fees/me');
    return data;
  },

  myEvents: async (): Promise<EventItem[]> => {
    // /events/me renvoie une réponse paginée { data, meta } (events.controller.ts).
    const { data } = await api.get<PaginatedResponse<EventItem>>('/events/me');
    return data.data;
  },

  /**
   * URL d'export .ics (Google/Apple/Outlook) — endpoint PUBLIC, pas de Bearer
   * requis, donc utilisable directement en `<a href>` (contrairement aux
   * exports authentifiés type portefeuille/enquête qui doivent passer par un blob).
   */
  icsUrl: (id: string): string => `${api.defaults.baseURL}/events/${id}/ics`,
};
